'use client'
import { useEffect } from 'react'
import Snap from 'lenis/snap'
import { useLenis } from './LenisProvider'

/**
 * Homepage section-snapping.
 *
 * Registers each top-level `<section>` under #main as a snap point on the
 * shared Lenis instance, so a hard scroll settles onto a section boundary
 * instead of overshooting to the bottom of the page.
 *
 * `type: 'proximity'` is the soft variant: you can still scroll freely through
 * tall sections, but when the scroll comes to rest within `distanceThreshold`
 * of a section start, Lenis eases it onto that start. It does NOT lock one
 * section per gesture — to make it firmer, change SNAP_TYPE to 'mandatory'
 * (always lands on a section) or 'lock' (one section per gesture, slide-deck).
 *
 * Mounted only on the homepage (see app/page.tsx), so the scroll-scrubbed
 * Luxiva turntable on /products/luxiva is never affected.
 */

// Knob: 'proximity' (soft, current) | 'mandatory' (always lands on a section)
// | 'lock' (one section per scroll gesture).
const SNAP_TYPE = 'proximity' as const

export default function ScrollSnap() {
  const lenis = useLenis()

  useEffect(() => {
    if (!lenis) return

    const sections = Array.from(
      document.querySelectorAll<HTMLElement>('#main > section'),
    )
    if (sections.length === 0) return

    const snap = new Snap(lenis, {
      type: SNAP_TYPE,
      // Only engage when the rest position lands within ~18% of a viewport
      // from a section start — keeps mid-section reading free and avoids
      // yanking you forward when you deliberately stop partway down.
      distanceThreshold: '18%',
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    })

    // Element-based snaps re-measure themselves on resize/reflow (fonts and
    // images loading shift offsets), so we don't hand-compute stale pixel
    // values. `align: 'start'` lands each section top at the viewport top;
    // their built-in top padding clears the fixed navbar.
    sections.forEach((el) => snap.addElement(el, { align: ['start'] }))

    // The <footer> lives OUTSIDE #main and is shorter than the viewport, so it
    // was never a snap point — and with only #contact's top registered as the
    // last snap, resting near the bottom got yanked back up to #contact,
    // hiding the footer (social icons + back-to-top). Register the footer with
    // align 'end' so its snap value is `footerTop + footerHeight - viewport`
    // (≈ the page's max scroll). That makes "footer fully in view at the
    // bottom" a real rest position: at the bottom that end-value is always just
    // past max scroll, so it wins over #contact instead of pulling away — and
    // being element-based it re-measures on resize / RTL / language change.
    const footer = document.querySelector<HTMLElement>('footer')
    if (footer) snap.addElement(footer, { align: ['end'] })

    return () => snap.destroy()
  }, [lenis])

  return null
}
